import type {
  TaskTrackerBoardCompletedStatusFilter,
  TaskTrackerBoardStickerFilter,
} from '../store/taskTracker.types'

export type TaskTrackerStickerFilterRangeDraft = {
  from: string | null
  to: string | null
}

export type TaskTrackerStickerFilterDraftValue =
  | string
  | string[]
  | TaskTrackerStickerFilterRangeDraft
  | null

export type TaskTrackerStickerFiltersDraft = Record<
  number,
  TaskTrackerStickerFilterDraftValue
>

export const FILTERABLE_TASK_TRACKER_STICKER_FIELD_TYPES = new Set([
  'text',
  'date',
  'datetime',
  'number',
  'select',
  'performer',
  'deadline',
  'task_created_at',
  'task_completed_at',
])

export const TASK_TRACKER_SYSTEM_STICKER_FIELD_TYPES = new Set([
  'performer',
  'deadline',
  'task_created_at',
  'task_completed_at',
])

export const TASK_TRACKER_SYSTEM_DATETIME_STICKER_FIELD_TYPES = new Set([
  'task_created_at',
  'task_completed_at',
])

export const TASK_TRACKER_STICKER_FILTER_RANGE_FIELD_TYPES = new Set([
  'date',
  'datetime',
  'number',
  'deadline',
  'task_created_at',
  'task_completed_at',
])

export const TASK_TRACKER_DATE_LIKE_STICKER_FIELD_TYPES = new Set([
  'date',
  'datetime',
  'deadline',
  'task_created_at',
  'task_completed_at',
])

export const TASK_TRACKER_DATETIME_LIKE_STICKER_FIELD_TYPES = new Set([
  'datetime',
  'task_created_at',
  'task_completed_at',
])

export const isTaskTrackerStickerFieldFilterable = (fieldType: string) =>
  FILTERABLE_TASK_TRACKER_STICKER_FIELD_TYPES.has(fieldType)

export const isTaskTrackerSystemStickerFieldType = (fieldType: string) =>
  TASK_TRACKER_SYSTEM_STICKER_FIELD_TYPES.has(fieldType)

export const isTaskTrackerSystemDatetimeStickerFieldType = (
  fieldType: string | null | undefined,
  systemKey?: string | null,
) => {
  const type = String(fieldType ?? '').trim()
  const key = String(systemKey ?? '').trim()

  return (
    TASK_TRACKER_SYSTEM_DATETIME_STICKER_FIELD_TYPES.has(type) ||
    TASK_TRACKER_SYSTEM_DATETIME_STICKER_FIELD_TYPES.has(key)
  )
}

export const isTaskTrackerDateLikeStickerFieldType = (fieldType: string) =>
  TASK_TRACKER_DATE_LIKE_STICKER_FIELD_TYPES.has(fieldType)

export const isTaskTrackerDateTimeLikeStickerFieldType = (fieldType: string) =>
  TASK_TRACKER_DATETIME_LIKE_STICKER_FIELD_TYPES.has(fieldType)

export const isTaskTrackerStickerFilterRangeFieldType = (fieldType: string) =>
  TASK_TRACKER_STICKER_FILTER_RANGE_FIELD_TYPES.has(fieldType)

export const isTaskTrackerStickerFilterDebouncedFieldType = (fieldType: string) =>
  fieldType === 'text' || fieldType === 'number'

export const isTaskTrackerStickerFilterRangeDraft = (
  value: TaskTrackerStickerFilterDraftValue | undefined,
): value is TaskTrackerStickerFilterRangeDraft =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('from' in value || 'to' in value)

export const createEmptyTaskTrackerStickerFilterRangeDraft =
  (): TaskTrackerStickerFilterRangeDraft => ({
    from: null,
    to: null,
  })

export const normalizeTaskTrackerStickerFilterValue = (
  value: string | null | undefined,
): string | null => {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed || null
}

export const normalizeTaskTrackerStickerFilterRangeValue = (
  value: string | number | null | undefined,
): string | null => {
  if (value == null) return null

  const normalized = normalizeTaskTrackerStickerFilterValue(String(value))
  if (!normalized) return null

  return normalized.replace(
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}):\d{2}$/,
    '$1',
  )
}

export const isTaskTrackerBoardStickerFieldSettingId = (
  fieldSettingId: unknown,
): fieldSettingId is number => {
  const id = Number(fieldSettingId)
  return Number.isFinite(id) && id > 0
}

export const normalizeTaskTrackerStickerFilterValues = (
  values: string[] | string | number | null | undefined,
): string[] => {
  if (values == null || typeof values === 'function') return []

  if (Array.isArray(values)) {
    return values
      .map((value) => String(value).trim())
      .filter(Boolean)
  }

  const scalar = String(values).trim()
  return scalar ? [scalar] : []
}

const normalizeBoardStickerFilterEchoMultiValue = (value: unknown): string[] => {
  if (value == null || typeof value === 'function') return []

  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean)
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const scalar = String(value).trim()
    return scalar ? [scalar] : []
  }

  return []
}

type BoardStickerFilterEchoItem = {
  field_setting_id?: number | string | null
  type?: string | null
  value?: unknown
  values?: unknown
  from?: string | number | null
  to?: string | number | null
}

const isBoardStickerFilterEchoItem = (
  value: unknown,
): value is BoardStickerFilterEchoItem => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const fieldSettingId = Number(
    (value as BoardStickerFilterEchoItem).field_setting_id,
  )
  return Number.isFinite(fieldSettingId) && fieldSettingId > 0
}

const ensureBoardStickerFilterEchoArray = (
  items: unknown,
): BoardStickerFilterEchoItem[] => {
  if (items == null) return []

  if (Array.isArray(items)) {
    return items.filter(isBoardStickerFilterEchoItem)
  }

  if (isBoardStickerFilterEchoItem(items)) {
    return [items]
  }

  if (typeof items === 'object') {
    return Object.values(items).filter(isBoardStickerFilterEchoItem)
  }

  return []
}

export const mapBoardStickerFilterEchoItemFromApi = (
  item: BoardStickerFilterEchoItem,
): TaskTrackerBoardStickerFilter | null => {
  const fieldSettingId = Number(item.field_setting_id)
  if (!isTaskTrackerBoardStickerFieldSettingId(fieldSettingId)) return null

  const echoType = String(item.type ?? '').trim().toLowerCase()
  const from = normalizeTaskTrackerStickerFilterRangeValue(item.from)
  const to = normalizeTaskTrackerStickerFilterRangeValue(item.to)

  if (from != null || to != null || echoType === 'range') {
    if (from == null && to == null) return null

    return {
      fieldSettingId,
      type: item.type ? String(item.type) : null,
      from,
      to,
    }
  }

  const values = normalizeBoardStickerFilterEchoMultiValue(item.values)
  const valuesFromEchoValue = normalizeBoardStickerFilterEchoMultiValue(item.value)

  const mergedValues =
    values.length > 0
      ? values
      : echoType === 'performer' || echoType === 'select' || Array.isArray(item.value)
        ? valuesFromEchoValue
        : []

  if (mergedValues.length > 0) {
    return {
      fieldSettingId,
      type: item.type ? String(item.type) : null,
      values: mergedValues,
    }
  }

  const scalarValue = normalizeTaskTrackerStickerFilterValue(
    typeof item.value === 'string' || typeof item.value === 'number'
      ? String(item.value)
      : null,
  )

  if (scalarValue == null) return null

  return {
    fieldSettingId,
    type: item.type ? String(item.type) : null,
    value: scalarValue,
  }
}

export const mapBoardStickerFiltersFromApi = (
  items: unknown,
): TaskTrackerBoardStickerFilter[] =>
  sanitizeTaskTrackerBoardStickerFilters(
    ensureBoardStickerFilterEchoArray(items)
      .map(mapBoardStickerFilterEchoItemFromApi)
      .filter((item): item is TaskTrackerBoardStickerFilter => item != null),
  )

export const isTaskTrackerStickerFilterDraftEmpty = (
  value: TaskTrackerStickerFilterDraftValue | undefined,
) => {
  if (value == null) return true

  if (typeof value === 'string') {
    return normalizeTaskTrackerStickerFilterValue(value) == null
  }

  if (Array.isArray(value)) {
    return normalizeTaskTrackerStickerFilterValues(value).length === 0
  }

  if (isTaskTrackerStickerFilterRangeDraft(value)) {
    return (
      normalizeTaskTrackerStickerFilterValue(value.from) == null &&
      normalizeTaskTrackerStickerFilterValue(value.to) == null
    )
  }

  return true
}

export const isTaskTrackerBoardStickerFilterEmpty = (
  filter: TaskTrackerBoardStickerFilter,
) => {
  if (normalizeTaskTrackerStickerFilterValues(filter.values).length > 0) {
    return false
  }

  if (normalizeTaskTrackerStickerFilterValue(filter.value) != null) {
    return false
  }

  return (
    normalizeTaskTrackerStickerFilterValue(filter.from) == null &&
    normalizeTaskTrackerStickerFilterValue(filter.to) == null
  )
}

export const TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Все', title: 'Все' },
  { value: 'completed', label: 'Выполнена', title: 'Выполнена' },
  { value: 'not_completed', label: 'Не выполнена', title: 'Не выполнена' },
] as const

export const DEFAULT_TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER: TaskTrackerBoardCompletedStatusFilter =
  'all'

export const resolveTaskTrackerBoardIsCompletedQuery = (
  status: TaskTrackerBoardCompletedStatusFilter,
): boolean | undefined => {
  if (status === 'completed') return true
  if (status === 'not_completed') return false
  return undefined
}

export const mapApiIsCompletedToCompletedStatusFilter = (
  value: boolean | null | undefined,
): TaskTrackerBoardCompletedStatusFilter => {
  if (value === true) return 'completed'
  if (value === false) return 'not_completed'
  return 'all'
}

export const serializeTaskTrackerBoardQueryFiltersKey = (
  stickerFilters: TaskTrackerBoardStickerFilter[],
  completedStatus: TaskTrackerBoardCompletedStatusFilter,
) =>
  JSON.stringify({
    stickers: JSON.parse(
      serializeTaskTrackerStickerFiltersApplied(stickerFilters),
    ),
    completedStatus,
  })

export const appendTaskTrackerBoardCompletedStatusToSearchParams = (
  searchParams: URLSearchParams,
  isCompleted?: boolean,
) => {
  if (isCompleted === undefined) return

  searchParams.set('is_completed', isCompleted ? 'true' : 'false')
}

export const appendTaskTrackerStickerFiltersToSearchParams = (
  searchParams: URLSearchParams,
  filters?: TaskTrackerBoardStickerFilter[],
) => {
  sanitizeTaskTrackerBoardStickerFilters(filters ?? []).forEach(
    (filter, index) => {
      searchParams.set(
        `sticker_filters[${index}][field_setting_id]`,
        String(filter.fieldSettingId),
      )

      const from = normalizeTaskTrackerStickerFilterRangeValue(filter.from)
      if (from) {
        searchParams.set(`sticker_filters[${index}][from]`, from)
      }

      const to = normalizeTaskTrackerStickerFilterRangeValue(filter.to)
      if (to) {
        searchParams.set(`sticker_filters[${index}][to]`, to)
      }

      const values = normalizeTaskTrackerStickerFilterValues(filter.values)
      if (values.length > 0) {
        values.forEach((value) => {
          searchParams.append(`sticker_filters[${index}][values][]`, value)
          // performer на бэке читает value[], select — values[]
          searchParams.append(`sticker_filters[${index}][value][]`, value)
        })
        return
      }

      const value = normalizeTaskTrackerStickerFilterValue(filter.value)
      if (value) {
        searchParams.set(`sticker_filters[${index}][value]`, value)
      }
    },
  )
}

export const mapStickerFiltersDraftToApi = (
  draft: TaskTrackerStickerFiltersDraft,
): TaskTrackerBoardStickerFilter[] => {
  const result: TaskTrackerBoardStickerFilter[] = []

  Object.entries(draft).forEach(([fieldSettingId, rawValue]) => {
    const parsedFieldSettingId = Number(fieldSettingId)
    if (!isTaskTrackerBoardStickerFieldSettingId(parsedFieldSettingId)) return

    if (isTaskTrackerStickerFilterDraftEmpty(rawValue)) return

    if (isTaskTrackerStickerFilterRangeDraft(rawValue)) {
      const from = normalizeTaskTrackerStickerFilterRangeValue(rawValue.from)
      const to = normalizeTaskTrackerStickerFilterRangeValue(rawValue.to)
      if (from == null && to == null) return

      result.push({
        fieldSettingId: parsedFieldSettingId,
        from,
        to,
      })
      return
    }

    if (Array.isArray(rawValue)) {
      const values = normalizeTaskTrackerStickerFilterValues(rawValue)
      if (values.length === 0) return

      result.push({
        fieldSettingId: parsedFieldSettingId,
        values,
      })
      return
    }

    const value = normalizeTaskTrackerStickerFilterValue(rawValue)
    if (value == null) return

    result.push({
      fieldSettingId: parsedFieldSettingId,
      value,
    })
  })

  return sanitizeTaskTrackerBoardStickerFilters(result)
}

const normalizeAppliedStickerFilter = (
  filter: TaskTrackerBoardStickerFilter,
): TaskTrackerBoardStickerFilter => {
  const values = normalizeTaskTrackerStickerFilterValues(filter.values)
  const from = normalizeTaskTrackerStickerFilterRangeValue(filter.from)
  const to = normalizeTaskTrackerStickerFilterRangeValue(filter.to)
  const value = normalizeTaskTrackerStickerFilterValue(filter.value)

  if (from != null || to != null) {
    return {
      fieldSettingId: filter.fieldSettingId,
      ...(from != null ? { from } : {}),
      ...(to != null ? { to } : {}),
    }
  }

  if (values.length > 0) {
    return {
      fieldSettingId: filter.fieldSettingId,
      values,
    }
  }

  if (value != null) {
    return {
      fieldSettingId: filter.fieldSettingId,
      value,
    }
  }

  return {
    fieldSettingId: filter.fieldSettingId,
  }
}

export const sanitizeTaskTrackerBoardStickerFilters = (
  filters: TaskTrackerBoardStickerFilter[],
): TaskTrackerBoardStickerFilter[] =>
  filters
    .filter((filter) =>
      isTaskTrackerBoardStickerFieldSettingId(filter.fieldSettingId),
    )
    .filter((filter) => !isTaskTrackerBoardStickerFilterEmpty(filter))
    .map(normalizeAppliedStickerFilter)

export const serializeTaskTrackerStickerFiltersDraft = (
  draft: TaskTrackerStickerFiltersDraft,
) => {
  const normalized = mapStickerFiltersDraftToApi(draft).map(
    normalizeAppliedStickerFilter,
  )

  normalized.sort((a, b) => a.fieldSettingId - b.fieldSettingId)

  return JSON.stringify(normalized)
}

export const serializeTaskTrackerStickerFiltersApplied = (
  filters: TaskTrackerBoardStickerFilter[],
) => {
  const normalized = sanitizeTaskTrackerBoardStickerFilters(filters).sort(
    (a, b) => a.fieldSettingId - b.fieldSettingId,
  )

  return JSON.stringify(normalized)
}

export const shouldSyncBoardStickerFiltersFromApi = (
  currentFilters: TaskTrackerBoardStickerFilter[],
  syncedFilters: TaskTrackerBoardStickerFilter[],
) => {
  const current = sanitizeTaskTrackerBoardStickerFilters(currentFilters)
  const synced = sanitizeTaskTrackerBoardStickerFilters(syncedFilters)

  // Бэк пока может отдавать {} для deadline — не затираем локальные фильтры
  if (synced.length === 0 && current.length > 0) {
    return false
  }

  if (synced.length > 0 && current.length > 0 && synced.length < current.length) {
    return false
  }

  return (
    serializeTaskTrackerStickerFiltersApplied(current) !==
    serializeTaskTrackerStickerFiltersApplied(synced)
  )
}

export const mapStickerFiltersAppliedToDraft = (
  filters: TaskTrackerBoardStickerFilter[],
): TaskTrackerStickerFiltersDraft => {
  const draft: TaskTrackerStickerFiltersDraft = {}

  sanitizeTaskTrackerBoardStickerFilters(filters).forEach((filter) => {
    const from = normalizeTaskTrackerStickerFilterRangeValue(filter.from)
    const to = normalizeTaskTrackerStickerFilterRangeValue(filter.to)
    if (from != null || to != null) {
      draft[filter.fieldSettingId] = { from, to }
      return
    }

    const values = normalizeTaskTrackerStickerFilterValues(filter.values)
    if (values.length > 0) {
      draft[filter.fieldSettingId] = values
      return
    }

    const value = normalizeTaskTrackerStickerFilterValue(filter.value)
    if (value != null) {
      draft[filter.fieldSettingId] = value
    }
  })

  return draft
}
